import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

// Get API URL from environment variable
const API_URL = window._env_ ? window._env_.API_URL : process.env.REACT_APP_API_URL;

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Set auth token
  const setAuthToken = (token) => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  };

  // Load user
  const loadUser = async () => {
    if (token) {
      setAuthToken(token);
      try {
        const res = await axios.get(`${API_URL}/auth/me`);
        setUser(res.data.data);
        setIsAuthenticated(true);
      } catch (err) {
        setToken(null);
        setUser(null);
        setIsAuthenticated(false);
        setAuthToken(null);
      }
    }
    setLoading(false);
  };

  // Register user
  const register = async (formData) => {
    try {
      const res = await axios.post(`${API_URL}/auth/register`, formData);
      const { token, user } = res.data;
      
      setToken(token);
      setAuthToken(token);
      setUser(user);
      setIsAuthenticated(true);
      
      return true;
    } catch (err) {
      // Keep the server's message on the error so the form can show it
      setError(err.response?.data?.error || 'Registration failed');
      throw err;
    }
  };

  const signIn = ({ token, user }) => {
    setToken(token);
    setAuthToken(token);
    setUser(user);
    setIsAuthenticated(true);
  };

  // Login user. Returns true when signed in, or { twoFactorToken } when a code from the
  // authenticator app is needed next (see completeTwoFactor)
  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API_URL}/auth/login`, { email, password });
      if (res.data.twoFactorRequired) return { twoFactorToken: res.data.twoFactorToken };
      if (res.data.twoFactorSetupRequired) return { twoFactorToken: res.data.twoFactorToken, setupRequired: true };
      signIn(res.data);
      return true;
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials');
      throw err;
    }
  };

  // Second step of signing in: a code from the authenticator app or a recovery code
  const completeTwoFactor = async (twoFactorToken, code) => {
    const res = await axios.post(`${API_URL}/auth/login/2fa`, { twoFactorToken, code });
    signIn(res.data);
    return res.data;
  };

  // Logout user. The server is told first, so an administrator's sign-out reaches the audit log;
  // the session ends either way.
  const logout = () => {
    if (token) {
      axios.post(`${API_URL}/auth/logout`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setAuthToken(null);
  };

  // Clear errors
  const clearError = () => {
    setError(null);
  };

  useEffect(() => {
    loadUser();
    // eslint-disable-next-line
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token,
        isAuthenticated,
        loading,
        user,
        error,
        register,
        login,
        completeTwoFactor,
        signIn,
        logout,
        clearError,
        updateUser: (updated) => setUser((prev) => ({ ...prev, ...updated }))
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
